/**
 * Live mask editing for MD3 fields (FND-14). Formatting rules live in `@saay/formatting`; this module
 * is the shared hook UI and buyer-mobile can use.
 */

export {
  applyMaskedInput,
  CARD_NUMBER_MASK,
  caretAfterDigitIndex,
  codeMask,
  digitIndexBeforeCaret,
  isAsciiDigit,
  MOBILE_MASK,
  NATIONAL_CODE_MASK,
  sanitizePastedDigits,
  SHEBA_DIGITS_MASK,
  SOLAR_DATE_MASK,
  TOMAN_AMOUNT_MASK,
  type MaskEditResult,
  type MaskSpec,
} from '@saay/formatting';

import { applyMaskedInput, type MaskSpec } from '@saay/formatting';
import { type ChangeEvent, type RefObject, useCallback, useLayoutEffect, useRef } from 'react';

export interface UseMaskedInputOptions {
  readonly onRawChange: (raw: string) => void;
  readonly raw: string;
  readonly spec: MaskSpec;
}

export interface UseMaskedInputResult {
  readonly display: string;
  readonly inputRef: RefObject<HTMLInputElement | null>;
  readonly onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

/** Caret-safe masked single-line input; `raw` is normalized ASCII digits (or amount digits). */
export function useMaskedInput({
  onRawChange,
  raw,
  spec,
}: UseMaskedInputOptions): UseMaskedInputResult {
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingCaret = useRef<{ readonly end: number; readonly start: number } | null>(null);
  const display = spec.formatDigits(raw.slice(0, spec.maxDigits));

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const target = event.target;
      const result = applyMaskedInput(
        spec,
        target.value,
        target.selectionStart ?? 0,
        target.selectionEnd ?? 0,
      );
      pendingCaret.current = { end: result.selectionEnd, start: result.selectionStart };
      onRawChange(result.raw);
    },
    [onRawChange, spec],
  );

  useLayoutEffect(() => {
    const pending = pendingCaret.current;
    const input = inputRef.current;
    if (pending === null || input === null) {
      return;
    }
    input.setSelectionRange(pending.start, pending.end);
    pendingCaret.current = null;
  }, [display]);

  return { display, inputRef, onChange };
}
